import { useCallback, useMemo } from 'react';

import { type ActiveItem, ItemType } from 'components/CasinoCategories';
import { CasinoGroupPathParam, CasinoSubpage } from 'utils/casino';
import { type Query, useSearchQuery } from 'utils/location';
import { useLocation, useParams } from 'wouter';

const emptyCategoriesQuery: Query = {
  category: null,
};

const getActiveItems = (
  categories: (string | null)[],
  group: CasinoGroupPathParam,
): ActiveItem[] => {
  const filteredCategories = categories.filter((id): id is string => !!id);

  if (filteredCategories.length > 0) {
    return filteredCategories.map((id) => ({ type: ItemType.category, id }));
  }

  if (group === CasinoGroupPathParam.LIVE) {
    return [
      {
        type: ItemType.live,
      },
    ];
  }

  return [
    {
      type: ItemType.all,
    },
  ];
};

const changeActiveItems = (item: ActiveItem): Query => {
  if (item.type !== ItemType.category) {
    return emptyCategoriesQuery;
  }

  return {
    category: item.id,
  };
};

const useCasinoCategories = (): {
  activeItems: ActiveItem[];
  onChange: (item: ActiveItem) => void;
} => {
  const [query, setQuery] = useSearchQuery();
  const [, navigate] = useLocation();

  const { group, subpage } = useParams<{
    group: CasinoGroupPathParam;
    subpage: CasinoSubpage;
  }>();

  const activeItems = useMemo<ActiveItem[]>(
    () => getActiveItems([query.get('category')], group),
    [query, group],
  );

  const onChange = useCallback(
    (item: ActiveItem) => {
      const newQuery = changeActiveItems(item);

      if (item.type === ItemType.all) {
        navigate('/casino');
        return;
      }

      if (item.type === ItemType.live) {
        navigate('/live-casino');
        return;
      }

      if (subpage === CasinoSubpage.FAVORITES) {
        const filteredQuery = Object.fromEntries(
          Object.entries(newQuery)
            .filter(([_, value]) => value !== null)
            .map(([key, value]) => [key, String(value)]),
        );

        const queryString = new URLSearchParams(filteredQuery).toString();
        navigate(`/${group}?${queryString}`);
        return;
      }

      setQuery(newQuery);
    },
    [group, subpage, setQuery, navigate],
  );

  return {
    activeItems,
    onChange,
  };
};

export default useCasinoCategories;
